import { useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';

import {
  Car,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Globe,
  Link2,
  Lock,
  Newspaper,
  Pencil,
  Plus,
  RefreshCw,
  Settings2,
  ShoppingBag,
  Star,
  Tag,
  Trash2,
  Unplug,
  Users,
} from 'lucide-react';

import { useAPIClient } from '@src/api';
import { Banner, Button, cn, StatusChip } from '@src/components/ui';
import { useActiveOrg, usePermissions } from '@src/context/hooks';
import { Form, TextField } from '@src/corelib/forms/generic';
import {
  useChannelDeleteMutation,
  useChannelReorderMutation,
  useChannelSaveMutation,
  useChannelsQuery,
  useChannelToggleMutation,
  useIntegrationDisconnectMutation,
  useIntegrationLimitsQuery,
  useIntegrationOnboardMutation,
  useIntegrationRemoveMutation,
  useIntegrationRenameMutation,
  useIntegrationSetDefaultMutation,
  useIntegrationsQuery,
} from '@src/query';

import { Toaster, useToasts } from '../Listings/useToasts';
import {
  channelDefaults,
  channelSchema,
  connectionRenameDefaults,
  connectionRenameSchema,
} from './channelForm';
import { ConfirmDialog, firstApiError, Modal, Switch } from './controls';
import {
  canAddEbay,
  canSetDefaultEbay,
  ebayAccountLabel,
  ebayLimitsFor,
  ebayPoliciesReady,
  ebayReturnToast,
  needsReconnect,
  shouldShowNoDefaultBanner,
  visibleSettingsChannels,
} from './ebayConnect';
import EbayConnectModal from './EbayConnectModal';
import PageHeader from './PageHeader';
import ReturnWindowSettings from './ReturnWindowSettings';


/** Built-in channel presentation (icon + sub-label) keyed by the backend
 * `key`. Custom channels fall back to a link icon + their URL/host. */
const BUILTIN_META = {
  own: { icon: Globe, description: 'Your AutoOrbit storefront' },
  ebay: { icon: Tag, description: 'Listing copy + tracking' },
  facebook: { icon: Users, description: 'Listing copy + tracking' },
  craigslist: { icon: Newspaper, description: 'Tracking only' },
  carpart: { icon: Car, description: 'Used-parts network · tracking' },
};


function channelDescription(channel) {
  if (channel.is_builtin) {
    return BUILTIN_META[channel.key]?.description || 'Tracking only';
  }
  return channel.url || 'Custom channel · tracking only';
}


function ChannelRow(props) {
  const { channel, canWrite, busy, isFirst, isLast, onToggle, onMove, onEdit, onDelete } = props;
  const Icon = channel.is_builtin ? (BUILTIN_META[channel.key]?.icon || Tag) : Link2;
  const enabled = Boolean(channel.enabled);

  return (
    <div className="flex items-center gap-3.5 rounded-[11px] border border-hairline bg-surface-1 px-4 py-3.5">
      {canWrite
        ? (
          <span className="flex flex-col">
            <Button
              size="icon"
              type="button"
              variant="ghost"
              disabled={isFirst || busy}
              aria-label={`Move ${channel.name} up`}
              className="size-auto bg-transparent p-0 text-ink-subtle hover:bg-transparent hover:text-ink"
              onClick={() => onMove(-1)}
            >
              <ChevronUp className="size-4" />
            </Button>

            <Button
              size="icon"
              type="button"
              variant="ghost"
              disabled={isLast || busy}
              aria-label={`Move ${channel.name} down`}
              className="size-auto bg-transparent p-0 text-ink-subtle hover:bg-transparent hover:text-ink"
              onClick={() => onMove(1)}
            >
              <ChevronDown className="size-4" />
            </Button>
          </span>
        )
        : null}

      <span
        className={cn(
          'grid size-[38px] shrink-0 place-items-center rounded-[9px]',
          enabled ? 'bg-primary/13 text-primary' : 'bg-surface-2 text-ink-subtle',
        )}
      >
        <Icon className="size-[18px]" />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-sm font-semibold text-ink">{channel.name}</span>
        <span className="truncate text-xs text-ink-subtle">{channelDescription(channel)}</span>
      </span>

      {canWrite && !channel.is_builtin
        ? (
          <span className="flex items-center gap-1">
            <Button
              size="icon"
              type="button"
              variant="ghost"
              aria-label={`Edit ${channel.name}`}
              className="size-8 text-ink-subtle hover:bg-surface-2"
              onClick={onEdit}
            >
              <Pencil className="size-[15px]" />
            </Button>

            <Button
              size="icon"
              type="button"
              variant="ghost"
              aria-label={`Delete ${channel.name}`}
              className="size-8 text-ink-subtle hover:bg-surface-2"
              onClick={onDelete}
            >
              <Trash2 className="size-[15px]" />
            </Button>
          </span>
        )
        : null}

      <Switch
        disabled={busy}
        checked={enabled}
        readOnly={!canWrite}
        label={`${enabled ? 'Disable' : 'Enable'} ${channel.name}`}
        onChange={() => onToggle(!enabled)}
      />
    </div>
  );
}

ChannelRow.displayName = 'ChannelRow';


/** Add / edit a custom channel (name + optional URL). The `<Form>` carries an
 * `id` so the footer's submit Button (outside the FormProvider) can target it
 * via `form=`. The page's onSubmit rebuilds the original conditional payload. */
function ChannelEditor({ channel, busy, onSubmit, onError, onClose }) {
  const editing = Boolean(channel);

  return (
    <Modal
      title={editing ? 'Edit channel' : 'Add custom channel'}
      subtitle="Manual tracking only — AutoOrbit never posts to it for you."
      onClose={onClose}
      footer={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" loading={busy} form="channel-form">
            {editing ? 'Save' : 'Add channel'}
          </Button>
        </>
      )}
    >
      <Form
        id="channel-form"
        schema={channelSchema}
        defaultValues={channelDefaults(channel)}
        onError={onError}
        onSubmit={onSubmit}
      >
        <TextField name="name" label="Channel name" placeholder="OfferUp, local forum…" />
        <TextField name="url" label="URL (optional)" placeholder="https://…" />
      </Form>
    </Modal>
  );
}

ChannelEditor.displayName = 'ChannelEditor';


/** Rename an eBay connection (Plan 3, Slice E): a single nickname field so
 * multiple accounts are distinguishable on the part Listings tab. Mirrors
 * `ChannelEditor` — the `<Form>` carries an `id` the footer submit Button targets
 * via `form=`; the page's onSubmit PATCHes the trimmed label. */
function EbayRenameModal({ connection, busy, onSubmit, onError, onClose }) {
  return (
    <Modal
      title="Rename eBay account"
      subtitle="A nickname to tell your eBay accounts apart on the Listings tab."
      onClose={onClose}
      footer={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>

          <Button type="submit" loading={busy} form="ebay-rename-form">
            Save
          </Button>
        </>
      )}
    >
      <Form
        id="ebay-rename-form"
        schema={connectionRenameSchema}
        defaultValues={connectionRenameDefaults(connection)}
        onError={onError}
        onSubmit={onSubmit}
      >
        <TextField name="label" label="Account name" placeholder="Storefront, parts-only…" />
      </Form>
    </Modal>
  );
}

EbayRenameModal.displayName = 'EbayRenameModal';


/** eBay connection-status → StatusChip variant + label (.pen R2CDd card states).
 * `connected` reads as success, `expired` warns (token lapsed → needs reconnect),
 * `error` is destructive, `disconnected` is muted/neutral. */
const EBAY_STATUS = {
  connected: { variant: 'success', label: 'Connected' },
  expired: { variant: 'warning', label: 'Expired' },
  error: { variant: 'danger', label: 'Error' },
  disconnected: { variant: 'neutral', label: 'Disconnected' },
};


/** eBay connection card (.pen PnMum/ZVwHn + R2CDd states): nickname, eBay
 * username, status chip, Default badge, and business-policy rows (display-only).
 * Owner/Manager (`canManage`) get inline actions — "Set as default" (only for a
 * connected, non-default account) and Disconnect (⋯-equivalent, consistent with
 * ChannelRow's inline edit/delete). Lister/Viewer see the same card read-only. */
function EbayAccountCard(props) {
  const { connection, canManage, busy, onSetDefault, onDisconnect, onRemove, onRename, onOnboard, onReconnect } = props;
  const status = EBAY_STATUS[connection.status] || EBAY_STATUS.disconnected;
  const policiesReady = ebayPoliciesReady(connection);
  const showSetDefault = canManage && canSetDefaultEbay(connection);
  const showReconnect = canManage && needsReconnect(connection.status);
  const showDisconnect = canManage && connection.status !== 'disconnected';
  // The setup action needs a live token; hide it on disconnected accounts (the
  // backend rejects them 409 anyway). Owner/Manager only.
  const showOnboard = canManage && connection.status !== 'disconnected';
  const accountName = connection.external_account || connection.label || 'eBay account';

  return (
    <div className="flex flex-col gap-3.5 rounded-[11px] border border-hairline bg-surface-1 px-4 py-3.5">
      <div className="flex items-center gap-3.5">
        <span className="grid size-[38px] shrink-0 place-items-center rounded-[9px] bg-primary/13 text-primary">
          <ShoppingBag className="size-[18px]" />
        </span>

        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-ink">
              {connection.label || connection.external_account || 'eBay account'}
            </span>

            {connection.is_default
              ? (
                <StatusChip variant="primary" className="px-2 py-[3px] text-[11px]" bordered={false}>
                  Default
                </StatusChip>
              )
              : null}
          </span>

          <span className="truncate text-xs text-ink-subtle">
            {connection.external_account || 'eBay seller account'}
          </span>
        </span>

        <StatusChip variant={status.variant}>{status.label}</StatusChip>

        {canManage || showSetDefault || showReconnect || showDisconnect
          ? (
            <span className="flex items-center gap-1">
              {canManage
                ? (
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Rename ${accountName}`}
                    className="size-8 text-ink-subtle hover:bg-surface-2"
                    onClick={() => onRename(connection)}
                  >
                    <Pencil className="size-[15px]" />
                  </Button>
                )
                : null}

              {showSetDefault
                ? (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    className="h-8 gap-1.5 px-2.5 text-xs text-ink-subtle hover:bg-surface-2 hover:text-ink"
                    onClick={() => onSetDefault(connection)}
                  >
                    <Star className="size-[15px]" />
                    Set as default
                  </Button>
                )
                : null}

              {showReconnect
                ? (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    className="h-8 gap-1.5 px-2.5 text-xs text-warning hover:bg-surface-2 hover:text-warning"
                    onClick={() => onReconnect(connection)}
                  >
                    <RefreshCw className="size-[15px]" />
                    Reconnect
                  </Button>
                )
                : null}

              {showDisconnect
                ? (
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Disconnect ${accountName}`}
                    className="size-8 text-ink-subtle hover:bg-surface-2"
                    onClick={() => onDisconnect(connection)}
                  >
                    <Unplug className="size-[15px]" />
                  </Button>
                )
                : null}

              {canManage
                ? (
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Remove ${accountName}`}
                    className="size-8 text-ink-subtle hover:bg-surface-2"
                    onClick={() => onRemove(connection)}
                  >
                    <Trash2 className="size-[15px]" />
                  </Button>
                )
                : null}
            </span>
          )
          : null}
      </div>

      <div className="flex items-center gap-3 rounded-[9px] border border-hairline bg-surface-2/60 px-3.5 py-3">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          {policiesReady
            ? (
              <>
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-success">
                  <CheckCircle2 className="size-[15px] shrink-0" />
                  Business policies ready
                </span>

                <span className="text-xs text-ink-subtle">
                  Merchant location: needs eBay seller registration
                </span>
              </>
            )
            : (
              <>
                <span className="text-[13px] font-semibold text-ink">Finish eBay setup</span>

                <span className="text-xs text-ink-subtle">
                  Create your eBay business policies to get ready to list.
                </span>
              </>
            )}
        </span>

        {showOnboard
          ? (
            <Button
              type="button"
              disabled={busy}
              variant="secondary"
              className="h-8 shrink-0 gap-1.5 text-xs"
              loading={busy}
              onClick={() => onOnboard(connection)}
            >
              <Settings2 className="size-[15px]" />
              {policiesReady ? 'Re-run setup' : 'Finish eBay setup'}
            </Button>
          )
          : null}
      </div>
    </div>
  );
}

EbayAccountCard.displayName = 'EbayAccountCard';


/** Disabled lock-tile upsell shown in place of the Add button when the org is at
 * its eBay plan cap (.pen R2CDd "locked" state). Not clickable — a quiet prompt
 * to upgrade. */
function EbayLockTile() {
  return (
    <Button
      disabled
      type="button"
      variant="secondary"
      className="h-[38px] gap-1.5 text-[13px]"
    >
      <Lock className="size-[15px]" />
      Upgrade for more
    </Button>
  );
}

EbayLockTile.displayName = 'EbayLockTile';


/** eBay accounts section (.pen R2CDd "eBay Connect — Flow"): lists the org's
 * connected eBay accounts as read-only cards, with the Add affordance (stage 2
 * connect modal) gated on the plan caps — primary "Add" when `can_add`, a locked
 * "Upgrade for more" tile when at the cap. The empty (Not-connected) state offers
 * the same connect CTA. Reads are open to any member; the Add affordances and
 * queries are gated on `canManage`. */
function EbaySection({ sellerId, canManage, onToast }) {
  const apiClient = useAPIClient();
  const enabled = Boolean(sellerId) && canManage;
  const integrationsQuery = useIntegrationsQuery(sellerId, { enabled });
  const limitsQuery = useIntegrationLimitsQuery(sellerId, { enabled });

  const [connectOpen, setConnectOpen] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const [pendingRename, setPendingRename] = useState(null);

  // The connection mutations invalidate the shared integrations prefix, which by
  // TanStack's default prefix matching refreshes the connections list AND the
  // limits/counts in one call (set-default moves a badge; disconnect frees a cap
  // slot; rename updates the card + the part Listings tab row label).
  const integrationsKey = ['root', '', 'integrations', sellerId];
  const setDefaultMutation = useIntegrationSetDefaultMutation();
  const disconnectMutation = useIntegrationDisconnectMutation();
  const removeMutation = useIntegrationRemoveMutation();
  const renameMutation = useIntegrationRenameMutation();
  const onboardMutation = useIntegrationOnboardMutation();
  const actionBusy = setDefaultMutation.isPending
    || disconnectMutation.isPending
    || removeMutation.isPending
    || renameMutation.isPending
    || onboardMutation.isPending;

  const connections = integrationsQuery.data || [];
  const ebayConnections = connections.filter((connection) => connection.type === 'ebay');
  const showNoDefaultBanner = shouldShowNoDefaultBanner(ebayConnections);

  const setDefault = (connection) => {
    setDefaultMutation.mutate(
      { sellerId, connectionId: connection.id, queryKey: integrationsKey },
      {
        onSuccess: () => onToast('Default eBay account updated'),
        onError: () => onToast('Could not set default eBay account', 'danger'),
      },
    );
  };

  const runOnboard = (connection) => {
    onboardMutation.mutate(
      { sellerId, connectionId: connection.id, queryKey: integrationsKey },
      {
        onSuccess: () => onToast('eBay business policies ready'),
        onError: () => onToast('eBay setup failed — try again', 'danger'),
      },
    );
  };

  const confirmDisconnect = () => {
    disconnectMutation.mutate(
      { sellerId, connectionId: pendingDisconnect.id, queryKey: integrationsKey },
      {
        onSuccess: () => {
          setPendingDisconnect(null);
          onToast('eBay account disconnected');
        },
        onError: () => {
          setPendingDisconnect(null);
          onToast('Could not disconnect eBay account', 'danger');
        },
      },
    );
  };

  const confirmRemove = () => {
    removeMutation.mutate(
      { sellerId, connectionId: pendingRemove.id, queryKey: integrationsKey },
      {
        onSuccess: () => {
          setPendingRemove(null);
          onToast('eBay account removed');
        },
        onError: () => {
          setPendingRemove(null);
          onToast('Could not remove eBay account', 'danger');
        },
      },
    );
  };

  // Returns the mutateAsync promise so the Form engine maps 400 field errors
  // (e.g. label too long) back onto the input; close + toast on success.
  const submitRename = (values) => renameMutation
    .mutateAsync({
      sellerId,
      connectionId: pendingRename.id,
      label: values.label.trim(),
      queryKey: integrationsKey,
    })
    .then(() => {
      setPendingRename(null);
      onToast('eBay account renamed');
    });

  const limitsRow = ebayLimitsFor(limitsQuery.data);
  const countLabel = ebayAccountLabel(limitsRow);
  const canAdd = canAddEbay(limitsRow);
  // While limits load (no row yet) show the primary Add but disable it so it does
  // not flash a wrong state; swap to the lock tile once a row says can_add=false.
  const atCap = Boolean(limitsRow) && !canAdd;

  const continueToEbay = async () => {
    setConnecting(true);
    try {
      const resp = await apiClient.request({
        url: `/integrations/${sellerId}/connections/ebay/authorize-url/`,
        method: 'GET',
      });
      window.location.assign(resp.url);
    }
    catch (error) {
      setConnecting(false);
      setConnectOpen(false);
      if (error?.response?.status === 403) {
        onToast('Account limit reached — upgrade your plan', 'danger');
      }
      else {
        onToast('Couldn\'t start eBay connect', 'danger');
      }
    }
  };

  // Reconnect reuses the same consent flow: the backend appends `prompt=login`
  // so the seller re-authorizes the same eBay account (no per-account param,
  // and no connect modal — go straight to eBay).
  const reconnect = () => continueToEbay();

  const addButton = canManage
    ? atCap
      ? <EbayLockTile />
      : (
        <Button
          type="button"
          disabled={limitsQuery.isPending}
          className="h-[38px] gap-1.5 text-[13px]"
          onClick={() => setConnectOpen(true)}
        >
          <Plus className="size-[15px]" />
          Add eBay account
        </Button>
      )
    : null;

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3 px-4 lg:px-0">
        <span className="flex flex-col gap-0.5">
          <h2 className="text-sm font-semibold text-ink">eBay accounts</h2>

          {countLabel
            ? <span className="text-xs text-ink-subtle">{countLabel}</span>
            : null}
        </span>

        {addButton}
      </div>

      {showNoDefaultBanner
        ? (
          <Banner variant="warning" icon={<Star />}>
            No default eBay account — set one so new listings know where to publish.
          </Banner>
        )
        : null}

      {integrationsQuery.isPending
        ? <span className="py-6 text-center text-[13px] text-ink-subtle">Loading eBay accounts…</span>
        : integrationsQuery.isError
          ? <span className="py-6 text-center text-[13px] text-danger">Could not load eBay accounts.</span>
          : ebayConnections.length
            ? ebayConnections.map((connection) => (
              <EbayAccountCard
                busy={actionBusy}
                key={connection.id}
                canManage={canManage}
                onOnboard={runOnboard}
                connection={connection}
                onReconnect={reconnect}
                onSetDefault={setDefault}
                onRename={setPendingRename}
                onRemove={setPendingRemove}
                onDisconnect={setPendingDisconnect}
              />
            ))
            : (
              <div
                className={cn(
                  'flex flex-col items-center gap-3 rounded-[11px] border border-dashed',
                  'border-hairline-strong bg-surface-1 px-4 py-6 text-center',
                )}
              >
                <p className="text-[13px] text-ink-subtle">No eBay accounts connected yet</p>

                {canManage && canAdd
                  ? (
                    <Button
                      type="button"
                      className="h-[38px] gap-1.5 text-[13px]"
                      onClick={() => setConnectOpen(true)}
                    >
                      <Plus className="size-[15px]" />
                      Connect eBay account
                    </Button>
                  )
                  : canManage && atCap
                    ? <EbayLockTile />
                    : null}
              </div>
            )}

      {connectOpen
        ? (
          <EbayConnectModal
            busy={connecting}
            onContinue={continueToEbay}
            onClose={() => setConnectOpen(false)}
          />
        )
        : null}

      {pendingRename
        ? (
          <EbayRenameModal
            busy={renameMutation.isPending}
            onSubmit={submitRename}
            connection={pendingRename}
            onClose={() => setPendingRename(null)}
            onError={(error, meta) => {
              if (!meta?.isInvalid) {
                onToast('Could not rename eBay account', 'danger');
              }
            }}
          />
        )
        : null}

      {pendingDisconnect
        ? (
          <ConfirmDialog
            busy={disconnectMutation.isPending}
            title={`Disconnect ${pendingDisconnect.external_account || pendingDisconnect.label || 'eBay account'}?`}
            icon={Unplug}
            confirmLabel="Disconnect"
            onConfirm={confirmDisconnect}
            onCancel={() => setPendingDisconnect(null)}
            body="Listings stay but stop syncing. You can reconnect this account later."
          />
        )
        : null}

      {pendingRemove
        ? (
          <ConfirmDialog
            busy={removeMutation.isPending}
            title={`Remove ${pendingRemove.external_account || pendingRemove.label || 'eBay account'}?`}
            icon={Trash2}
            confirmLabel="Remove"
            onConfirm={confirmRemove}
            onCancel={() => setPendingRemove(null)}
            body="Removes the account from AutoOrbit and detaches its listings. Reconnect via eBay to restore it."
          />
        )
        : null}
    </section>
  );
}

EbaySection.displayName = 'EbaySection';


/** Channels (#156, #157, .pen PnMum): the org's sales channels — built-ins
 * (enable/disable/reorder, never deletable) plus custom rows. Owner/Manager
 * only; other roles are redirected. Manual tracking — no OAuth/API. */
function Channels() {
  const { toasts, push } = useToasts();
  const { seller } = useActiveOrg();
  const { canManageOrg: canManage } = usePermissions();

  const search = useSearch({ strict: false });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const channelsQuery = useChannelsQuery(seller?.id, { enabled: Boolean(seller) && canManage });
  const queryKey = channelsQuery.queryKey;

  const [editorState, setEditorState] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const toggleMutation = useChannelToggleMutation();
  const reorderMutation = useChannelReorderMutation();
  const saveMutation = useChannelSaveMutation();
  const deleteMutation = useChannelDeleteMutation();

  // eBay OAuth return (R2CDd): on ?ebay=<status>, toast the outcome, refresh the
  // integrations queries (connections + limits, by partial prefix), then strip
  // the param. Removing the param re-runs the effect with ebay undefined → no-op.
  const sellerId = seller?.id;
  useEffect(() => {
    if (!sellerId || !search.ebay) {
      return;
    }
    const toast = ebayReturnToast(search.ebay);
    if (toast) {
      push(toast.message, toast.variant);
    }
    queryClient.invalidateQueries({ queryKey: ['root', '', 'integrations', sellerId] });
    navigate({ to: '/app/settings/channels', search: {}, replace: true });
  }, [search.ebay, sellerId, push, navigate, queryClient]);

  if (!seller) {
    return null;
  }

  if (!canManage) {
    return <Navigate replace to="/app/settings" />;
  }

  const channels = channelsQuery.data || [];
  // Slice A: the four builtin toggle rows (ebay/facebook/craigslist/carpart) are
  // no longer rendered — only custom channels (and `own`) show here. The backend
  // SellerChannel rows stay seeded/enabled, so reorder must still POST the full
  // id set (views.py requires set(ids) == all org channels); `move` therefore
  // reorders the *visible* rows but rebuilds ids from the full `channels` array.
  const visibleChannels = visibleSettingsChannels(channels);

  const toggle = (channel, enabled) => {
    toggleMutation.mutate(
      { sellerId: seller.id, channelId: channel.id, enabled, queryKey },
      { onError: () => push(`Could not ${enabled ? 'enable' : 'disable'} ${channel.name}`, 'danger') },
    );
  };

  // `visibleIndex` is the row's position within `visibleChannels`. Swap it with
  // its adjacent visible neighbour, then emit ids in full-array order with that
  // single pair swapped — keeping hidden builtin rows in place and the id set
  // complete for the backend.
  const move = (visibleIndex, direction) => {
    const target = visibleIndex + direction;
    if (target < 0 || target >= visibleChannels.length) {
      return;
    }
    const fromId = visibleChannels[visibleIndex].id;
    const toId = visibleChannels[target].id;
    const next = channels.map((entry) => {
      if (entry.id === fromId) {
        return channels.find((candidate) => candidate.id === toId);
      }
      if (entry.id === toId) {
        return channels.find((candidate) => candidate.id === fromId);
      }
      return entry;
    });
    reorderMutation.mutate(
      { sellerId: seller.id, ids: next.map((entry) => entry.id), queryKey },
      { onError: () => push('Could not reorder channels', 'danger') },
    );
  };

  // Rebuild the original conditional payload from the Form values: always send
  // a trimmed `name`; include `url` only when non-empty after trimming. Returns
  // the mutateAsync promise so the Form engine maps 400 field errors onto the
  // inputs (and fires onError for server/no-response failures).
  const submitEditor = (values) => {
    const channelId = editorState?.channel?.id;
    const payload = { name: values.name.trim() };
    if (values.url.trim()) {
      payload.url = values.url.trim();
    }
    return saveMutation
      .mutateAsync({ sellerId: seller.id, channelId, payload, queryKey })
      .then(() => {
        setEditorState(null);
        push(channelId ? 'Channel updated' : 'Channel added');
      });
  };

  const confirmDelete = () => {
    deleteMutation.mutate(
      { sellerId: seller.id, channelId: pendingDelete.id, queryKey },
      {
        onSuccess: () => {
          setPendingDelete(null);
          push('Channel deleted');
        },
        onError: (error) => {
          setPendingDelete(null);
          push(firstApiError(error?.response?.data) || 'Could not delete channel', 'danger');
        },
      },
    );
  };

  const busy = toggleMutation.isPending || reorderMutation.isPending;

  const openEditor = (channel) => {
    setEditorState({ channel });
  };

  return (
    <div className="flex flex-1 flex-col gap-5 lg:p-7">
      <PageHeader
        title="Channels"
        subtitle="Where you track listings. No API — manual tracking only."
        actions={(
          <Button
            type="button"
            variant="secondary"
            className="h-[38px] gap-1.5 text-[13px]"
            onClick={() => openEditor(null)}
          >
            <Plus className="size-[15px]" />
            Add custom channel
          </Button>
        )}
      />

      <div className="flex w-full flex-col gap-2.5 px-4 pb-6 lg:px-0">
        {/* Mobile add button (the desktop one lives in PageHeader actions). */}
        <Button
          type="button"
          variant="secondary"
          className="h-11 gap-1.5 text-[13px] lg:hidden"
          onClick={() => openEditor(null)}
        >
          <Plus className="size-[15px]" />
          Add custom channel
        </Button>

        {channelsQuery.isPending
          ? <span className="py-10 text-center text-[13px] text-ink-subtle">Loading channels…</span>
          : channelsQuery.isError
            ? <span className="py-10 text-center text-[13px] text-danger">Could not load channels.</span>
            : visibleChannels.length
              ? visibleChannels.map((channel, index) => (
                <ChannelRow
                  busy={busy}
                  key={channel.id}
                  channel={channel}
                  canWrite={canManage}
                  isFirst={index === 0}
                  isLast={index === visibleChannels.length - 1}
                  onEdit={() => openEditor(channel)}
                  onDelete={() => setPendingDelete(channel)}
                  onMove={(direction) => move(index, direction)}
                  onToggle={(enabled) => toggle(channel, enabled)}
                />
              ))
              : (
                <div
                  className={cn(
                    'flex flex-col items-center gap-3 rounded-[11px] border border-dashed',
                    'border-hairline-strong bg-surface-1 px-4 py-6 text-center',
                  )}
                >
                  <p className="text-[13px] text-ink-subtle">No custom channels yet</p>

                  <Button
                    type="button"
                    variant="secondary"
                    className="h-[38px] gap-1.5 text-[13px]"
                    onClick={() => openEditor(null)}
                  >
                    <Plus className="size-[15px]" />
                    Add custom channel
                  </Button>
                </div>
              )}
      </div>

      <div className="flex w-full flex-col gap-2.5 px-4 pb-6 lg:px-0">
        <EbaySection sellerId={seller.id} canManage={canManage} onToast={push} />
      </div>

      <div className="flex w-full flex-col gap-2.5 px-4 pb-6 lg:px-0">
        <ReturnWindowSettings sellerId={seller.id} canWrite={canManage} onToast={push} />
      </div>

      {editorState
        ? (
          <ChannelEditor
            channel={editorState.channel}
            busy={saveMutation.isPending}
            onSubmit={submitEditor}
            onClose={() => setEditorState(null)}
            onError={(error, meta) => {
              if (!meta?.isInvalid) {
                push('Could not save channel', 'danger');
              }
            }}
          />
        )
        : null}

      {pendingDelete
        ? (
          <ConfirmDialog
            busy={deleteMutation.isPending}
            title={`Delete ${pendingDelete.name}?`}
            onConfirm={confirmDelete}
            onCancel={() => setPendingDelete(null)}
            body="This channel and its tracking columns are removed. Parts already tracked there keep their history."
          />
        )
        : null}

      <Toaster toasts={toasts} />
    </div>
  );
}

Channels.displayName = 'Channels';
export default Channels;
